import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {message} from "antd";
import {useLotteryApi} from "../api/lotteryApi";
import {cryptoRandomFloat} from "../random/cryptoRandom";
import {pickGenderTrack} from "./pickGenderTrack";
import {countPendingMeritWinners, pickMeritWinner} from "./pickMeritWinner";

// The marquee is decorative — it advertises that a big pool exists, it does not
// enumerate it. Sampling here keeps the DOM flat no matter how large the pool grows.
const MARQUEE_SAMPLE_PER_GENDER = 50;

// Module-level so the default argument keeps a stable identity across renders
// and cannot invalidate the memos below.
const NO_MERIT_WINNERS = [];

const countByGender = (winners, gender) => winners.filter(w => w.gender === gender).length;

/**
 * Owns the whole draw: server state, gender quotas, which pool the reel spins on,
 * employee merit winners, and the confirm handshake. The page above it stays
 * presentational.
 */
export const useLotteryDraw = ({maxMaleWinners, maxFemaleWinners, meritWinners = NO_MERIT_WINNERS}) => {
    const api = useLotteryApi();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [allLotteries, setAllLotteries] = useState([]);
    const [pool, setPool] = useState({M: [], F: []});
    const [poolLoaded, setPoolLoaded] = useState(false);
    const [winners, setWinners] = useState([]);
    const [currentTrack, setCurrentTrack] = useState([]);
    const [currentWinner, setCurrentWinner] = useState(null);
    const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);
    const [isFinalModalOpen, setIsFinalModalOpen] = useState(false);

    const finalModalTimer = useRef(null);

    // Total is always derived, so the per-gender quotas and the headline count
    // can never disagree.
    const maxWinners = maxMaleWinners + maxFemaleWinners;

    const load = useCallback(async () => {
        const [poolResult, winnersResult] = await Promise.all([
            api.fetchWinnableLotteries(),
            api.fetchWinners(),
        ]);

        if (poolResult.ok) {
            setPool(poolResult.data);
            setPoolLoaded(true);
            setAllLotteries([
                ...poolResult.data.F.slice(0, MARQUEE_SAMPLE_PER_GENDER).map(item => item.lottery),
                ...poolResult.data.M.slice(0, MARQUEE_SAMPLE_PER_GENDER).map(item => item.lottery),
            ]);
        }
        if (winnersResult.ok) setWinners(winnersResult.data);
    }, [api]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await load();
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [load]);

    useEffect(() => () => clearTimeout(finalModalTimer.current), []);

    /** Pull server state back in — the admin panel can delete winners mid-event. */
    const refresh = useCallback(async () => {
        setRefreshing(true);
        await load();
        setRefreshing(false);
    }, [load]);

    const maleWinners = countByGender(winners, 'M');
    const femaleWinners = countByGender(winners, 'F');

    /**
     * The tickets that may actually be drawn.
     *
     * `pool` is only what the server last said, and `winners` is only who has
     * already won — neither alone is authoritative. Reconciling them here, once,
     * is what makes drawing an existing winner's ticket impossible rather than
     * merely detected: such tickets never reach the reel in the first place.
     *
     * Matching on custno as well as lottery enforces one prize per customer, not
     * merely one prize per ticket.
     */
    const eligiblePool = useMemo(() => {
        const wonCustomers = new Set(winners.map(w => w.custno));
        const wonTickets = new Set(winners.map(w => w.lottery));
        const isDrawable = (item) => !wonCustomers.has(item.custno) && !wonTickets.has(item.lottery);

        return {M: pool.M.filter(isDrawable), F: pool.F.filter(isDrawable)};
    }, [pool, winners]);

    const isComplete = useMemo(() => (
        (maleWinners >= maxMaleWinners && femaleWinners >= maxFemaleWinners) ||
        // Only conclusive once we actually have the pool; an empty initial state
        // must not read as "finished".
        (poolLoaded && eligiblePool.M.length === 0 && eligiblePool.F.length === 0)
    ), [maleWinners, femaleWinners, maxMaleWinners, maxFemaleWinners, poolLoaded, eligiblePool.M.length, eligiblePool.F.length]);

    /**
     * The ticket the next spin is obliged to land on, or null for a draw by chance.
     *
     * Derived rather than latched: it recomputes from the eligible pool, so once
     * the merit winner is confirmed they stop being pending on their own and the
     * draw returns to chance without anything having to clear a flag.
     */
    const meritWinner = useMemo(() => pickMeritWinner({
        meritWinners,
        eligiblePool,
        maleWinners,
        femaleWinners,
        maxMaleWinners,
        maxFemaleWinners,
    }), [meritWinners, eligiblePool, maleWinners, femaleWinners, maxMaleWinners, maxFemaleWinners]);

    // Slots the spins drawn by chance must leave alone, so a merit winner still
    // waiting for their turn cannot have their gender's quota filled out from
    // under them.
    const pendingMerit = useMemo(
        () => countPendingMeritWinners({meritWinners, eligiblePool}),
        [meritWinners, eligiblePool]
    );

    // Choose which gender pool the next spin runs on.
    useEffect(() => {
        // A merit winner settles this too — spinning the other gender's pool
        // could never land on them.
        if (meritWinner) {
            setCurrentTrack(eligiblePool[meritWinner.gender].map(item => item.lottery));
            return;
        }

        const track = pickGenderTrack({
            malePoolSize: eligiblePool.M.length,
            femalePoolSize: eligiblePool.F.length,
            maleWinners,
            femaleWinners,
            maxMaleWinners,
            maxFemaleWinners,
            reservedMale: pendingMerit.M,
            reservedFemale: pendingMerit.F,
            random: () => cryptoRandomFloat(0, 1),
        });

        if (track === null) {
            setCurrentTrack([]);
            return;
        }
        setCurrentTrack(eligiblePool[track].map(item => item.lottery));
    }, [meritWinner, pendingMerit, eligiblePool, maleWinners, femaleWinners, maxMaleWinners, maxFemaleWinners]);

    /** Called by the reel once it settles on a ticket. */
    const selectWinner = useCallback((lottery) => {
        if (!lottery) return;

        // Resolved against the eligible set, so an already-won ticket cannot
        // resolve here at all. The old duplicate check is gone with it — it
        // returned silently, leaving the reel stopped on a number with no modal
        // and no explanation, which is the worst thing to do mid-event.
        const winner = eligiblePool.M.find(item => item.lottery === lottery)
            || eligiblePool.F.find(item => item.lottery === lottery);

        if (!winner) {
            // Only reachable if the pool shifted under an in-flight spin (a
            // refresh, or another operator saving). Say so and let them respin.
            console.error('Drawn ticket is no longer eligible:', lottery);
            message.warning('Энэ эрх сугалагдах боломжгүй боллоо. Дахин эргүүлнэ үү.');
            return;
        }

        setCurrentWinner(winner);
        setIsWinnerModalOpen(true);
    }, [eligiblePool]);

    /**
     * Persist first, then advance. A failed save leaves the modal open so the
     * operator can retry, rather than announcing a winner the server never got.
     */
    const confirmWinner = useCallback(async () => {
        if (!currentWinner || isSaving) return;

        setIsSaving(true);
        const saved = await api.saveWinner(currentWinner);
        setIsSaving(false);
        if (!saved) return;

        // No manual pool filtering: recording the winner is enough, because
        // `eligiblePool` derives from `winners`. Keeping a second, hand-maintained
        // copy of "who is still drawable" is what allowed the two to disagree.
        setWinners(prev => [...prev, currentWinner]);
        setIsWinnerModalOpen(false);
        setCurrentWinner(null);

        if (winners.length + 1 >= maxWinners) {
            finalModalTimer.current = setTimeout(() => setIsFinalModalOpen(true), 1000);
        }
    }, [api, currentWinner, isSaving, winners.length, maxWinners]);

    return {
        loading,
        refreshing,
        refresh,
        isSaving,
        allLotteries,
        currentTrack,
        meritWinner: meritWinner?.lottery ?? null,
        winners,
        maxWinners,
        isComplete,
        currentWinner,
        isWinnerModalOpen,
        isFinalModalOpen,
        openFinalModal: useCallback(() => setIsFinalModalOpen(true), []),
        closeFinalModal: useCallback(() => setIsFinalModalOpen(false), []),
        selectWinner,
        confirmWinner,
    };
};
